import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import {
  useUserContext,
  type TabKey,
} from 'talent_matching/contexts/user_context';

const PAGINATION_FIELDS = ['page', 'perPage', 'excludedIds'];

type UseFilterTableStateOptions = {
  tabId: TabKey;
  totalRows: number;
  isFetching?: boolean;
};

const useFilterTableState = ({
  tabId,
  totalRows,
  isFetching,
}: UseFilterTableStateOptions) => {
  const { activeTab } = useUserContext();
  const isActive = activeTab === tabId;

  const [mountKey, setMountKey] = useState(0);
  const previousIsActive = useRef(isActive);

  useEffect(() => {
    if (!previousIsActive.current && isActive) {
      setMountKey((k) => k + 1);
    }
    previousIsActive.current = isActive;
  }, [isActive]);

  const { watch, getValues, setValue } = useFormContext();
  const rowsPerPage = getValues('perPage');
  const currentPage = watch('page');

  const filterNames = Object.keys(getValues());
  const chosenFilterNames = filterNames.filter(
    (name) => !PAGINATION_FIELDS.includes(name)
  );
  const chosenFilters = watch(chosenFilterNames);
  const jsonChosenFilters = JSON.stringify(chosenFilters);

  const onPageChange = (newPage: number) => setValue('page', newPage + 1);
  const onRowsPerPageChange = useCallback(
    (newRowsPerPage: number) => {
      setValue('perPage', newRowsPerPage);
      setValue('page', 1);
    },
    [setValue]
  );

  useEffect(() => {
    setValue('page', 1);
  }, [jsonChosenFilters, setValue]);

  useEffect(() => {
    if (totalRows < (currentPage - 1) * rowsPerPage) {
      setValue('page', 1);
    }
  }, [currentPage, rowsPerPage, totalRows, setValue]);

  const showLoaderOnFetch = useRef(false);
  const prevFiltersRef = useRef(jsonChosenFilters);

  if (prevFiltersRef.current !== jsonChosenFilters) {
    showLoaderOnFetch.current = true;
    prevFiltersRef.current = jsonChosenFilters;
  }

  useEffect(() => {
    if (isFetching === false) {
      showLoaderOnFetch.current = false;
    }
  }, [isFetching]);

  const triggerLoader = useCallback(() => {
    showLoaderOnFetch.current = true;
  }, []);

  return {
    mountKey,
    currentPage,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
    showLoader: showLoaderOnFetch.current,
    triggerLoader,
  };
};

export default useFilterTableState;
